import { truncate } from 'lodash';

import { parse } from 'exifr';

/**
 * Reads the metadata a camera embeds in an image file (EXIF) into the fields the image pop-up
 * shows. exifr yields one merged object with translated tag names, numeric values and revived
 * Date objects; files in the wild carry partial, odd or corrupt tags, so every field is validated
 * and dropped rather than shown wrong. Nothing here touches the DOM, Knockout or i18n.
 */

export interface GpsPosition {
  latitude: number;
  longitude: number;
  /** Metres above sea level; negative below it. */
  altitude?: number;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

/** An EXIF tag name spaced out for reading, and its value as text. */
export interface MetadataTag {
  name: string;
  value: string;
}

export interface ImageMetadata {
  /** Make and model joined, without repeating a make the model already starts with. */
  camera?: string;
  lens?: string;
  software?: string;
  /**
   * EXIF dates carry no timezone, so exifr revives them as local time; they are displayed as the
   * camera's own wall clock, with `timeZoneOffset` naming its zone when the file records one.
   */
  dateTaken?: Date;
  /** The camera's UTC offset at capture time ("+02:00"), from OffsetTimeOriginal. */
  timeZoneOffset?: string;
  dimensions?: ImageDimensions;
  /** Seconds. */
  exposureTime?: number;
  fNumber?: number;
  iso?: number;
  /** Millimetres. */
  focalLength?: number;
  /** Millimetres. */
  focalLength35mm?: number;
  flashFired?: boolean;
  gps?: GpsPosition;
  /** Every readable tag, including the ones the typed fields above already name. */
  tags: MetadataTag[];
}

type RawExif = { [tag: string]: unknown };

/**
 * exifr's defaults already parse just the TIFF/EXIF/GPS blocks (XMP, ICC, IPTC, maker notes and
 * the thumbnail IFD stay off; an explicit `tiff: true` would switch the interop IFD on). The one
 * change keeps Flash numeric; dates are still revived into Date objects, and enumerated tags stay
 * numeric so the tag list shows what the file holds. The default `full` build is used on purpose: the smaller `lite` build cannot read PNG files
 * (their size header and optional `eXIf` chunk) and throws on a `pick` list, so PNGs would show
 * nothing at all.
 */
const EXIF_PARSE_OPTIONS = { translateValues: false };

/** Throws when the file is not an image exifr can read. */
export async function readImageMetadata(blob: Blob): Promise<ImageMetadata> {
  return normalizeExif(await parse(blob, EXIF_PARSE_OPTIONS));
}

const FLASH_FIRED_BIT = 1;
const ALTITUDE_BELOW_SEA_LEVEL = 1;

function asText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  return text.length > 0 ? text : undefined;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && isFinite(value) ? value : undefined;
}

function asValidDate(value: unknown): Date | undefined {
  return value instanceof Date && !isNaN(value.getTime()) ? value : undefined;
}

/** "+02:00" and "+02" are both written in the wild; anything else is not an offset. */
function asTimeZoneOffset(value: unknown): string | undefined {
  const text = asText(value);
  return text !== undefined && /^[+-]\d{2}(:?\d{2})?$/.test(text) ? text : undefined;
}

function formatCameraName(make: unknown, model: unknown): string | undefined {
  const makeText = asText(make);
  const modelText = asText(model);
  if (!modelText) {
    return makeText;
  }
  if (!makeText) {
    return modelText;
  }
  // Many vendors repeat themselves ("Canon" + "Canon EOS 5D"); compare on the first word of the
  // make so "NIKON CORPORATION" still matches "NIKON D750".
  const makeWord = makeText.split(/\s+/)[0].toLowerCase();
  if (modelText.toLowerCase().indexOf(makeWord) === 0) {
    return modelText;
  }
  return `${makeText} ${modelText}`;
}

function asDimensions(rawWidth: unknown, rawHeight: unknown): ImageDimensions | undefined {
  const width = asFiniteNumber(rawWidth);
  const height = asFiniteNumber(rawHeight);
  return width !== undefined && height !== undefined && width > 0 && height > 0
    ? { width, height }
    : undefined;
}

function readFlashFired(value: unknown): boolean | undefined {
  const flash = asFiniteNumber(value);
  return flash === undefined ? undefined : (flash & FLASH_FIRED_BIT) === FLASH_FIRED_BIT;
}

/** GPSAltitudeRef is a sea-level reference, not a unit: 1 means the value is a depth. */
function readAltitude(raw: RawExif): number | undefined {
  const altitude = asFiniteNumber(raw.GPSAltitude);
  if (altitude === undefined) {
    return undefined;
  }
  return asFiniteNumber(raw.GPSAltitudeRef) === ALTITUDE_BELOW_SEA_LEVEL ? -altitude : altitude;
}

function readGps(raw: RawExif): GpsPosition | undefined {
  const latitude = asFiniteNumber(raw.latitude);
  const longitude = asFiniteNumber(raw.longitude);
  // Android writes 0/0 when the camera had no fix.
  if (latitude === undefined || longitude === undefined || (latitude === 0 && longitude === 0)) {
    return undefined;
  }
  return { latitude, longitude, altitude: readAltitude(raw) };
}

/** Every field is optional; a file without usable EXIF yields an object with no tags. */
function normalizeExif(raw: RawExif | null | undefined): ImageMetadata {
  if (!raw) {
    return { tags: [] };
  }
  return {
    camera: formatCameraName(raw.Make, raw.Model),
    lens: asText(raw.LensModel),
    software: asText(raw.Software),
    dateTaken:
      asValidDate(raw.DateTimeOriginal) ?? asValidDate(raw.CreateDate) ?? asValidDate(raw.ModifyDate),
    timeZoneOffset: asTimeZoneOffset(raw.OffsetTimeOriginal) ?? asTimeZoneOffset(raw.OffsetTime),
    dimensions:
      asDimensions(raw.ExifImageWidth, raw.ExifImageHeight) ??
      asDimensions(raw.ImageWidth, raw.ImageHeight),
    exposureTime: asFiniteNumber(raw.ExposureTime),
    fNumber: asFiniteNumber(raw.FNumber),
    iso: asFiniteNumber(raw.ISO),
    focalLength: asFiniteNumber(raw.FocalLength),
    focalLength35mm: asFiniteNumber(raw.FocalLengthIn35mmFormat),
    flashFired: readFlashFired(raw.Flash),
    gps: readGps(raw),
    tags: readTags(raw),
  };
}

/** `latitude`/`longitude` are exifr's computed decimals, not tags the file carries. */
const HIDDEN_TAGS = ['MakerNote', 'latitude', 'longitude'];

/** lodash counts the ellipsis within this length. */
const MAX_TAG_VALUE_LENGTH = 200;
const TRUNCATE_OPTIONS = { length: MAX_TAG_VALUE_LENGTH, omission: '…' };
const TAG_DECIMALS = 6;
/** Binary EXIF payloads (version bytes, component layouts) decode to control characters. */
const CONTROL_CHARACTERS = /[\x00-\x1f\x7f]/;

/**
 * "PhotographicSensitivity" -> "Photographic Sensitivity", "GPSLatitude" -> "GPS Latitude",
 * "FocalLengthIn35mmFormat" -> "Focal Length In 35mm Format".
 */
function humanizeTagName(tag: string): string {
  return tag
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2');
}

/** Fixed decimals with trailing zeros dropped: 1.50 -> "1.5", 72 -> "72". */
function trimDecimals(value: number, decimals: number): string {
  return String(parseFloat(value.toFixed(decimals)));
}

/** EXIF dates carry no zone; they are the camera's wall clock and are shown as written. */
function formatTagDate(value: Date): string {
  const pad = (part: number) => String(part).padStart(2, '0');
  const date = `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  return `${date} ${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`;
}

/** Undefined for anything that would not read as text: byte payloads, nested objects, empties. */
function formatTagValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const text = asText(value);
    return text !== undefined && !CONTROL_CHARACTERS.test(text) ? text : undefined;
  }
  if (typeof value === 'number') {
    return isFinite(value) ? trimDecimals(value, TAG_DECIMALS) : undefined;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : formatTagDate(value);
  }
  // Rationals such as GPSLatitude arrive as arrays of numbers.
  if (Array.isArray(value)) {
    const parts = value.map(formatTagValue);
    return parts.length > 0 && parts.every((part) => part !== undefined)
      ? parts.join(', ')
      : undefined;
  }
  return undefined;
}

function readTags(raw: RawExif): MetadataTag[] {
  return Object.keys(raw)
    .filter((tag) => HIDDEN_TAGS.indexOf(tag) === -1)
    .flatMap((tag) => {
      const value = formatTagValue(raw[tag]);
      return value === undefined
        ? []
        : [{ name: humanizeTagName(tag), value: truncate(value, TRUNCATE_OPTIONS) }];
    })
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function formatExposureTime(seconds: number): string {
  if (seconds < 1) {
    return `1/${Math.round(1 / seconds)} s`;
  }
  return `${trimDecimals(seconds, 1)} s`;
}

export function formatFNumber(fNumber: number): string {
  return `f/${trimDecimals(fNumber, 1)}`;
}

export function formatFocalLength(millimetres: number): string {
  return `${trimDecimals(millimetres, 1)} mm`;
}

export function formatAltitude(metres: number): string {
  return `${Math.round(metres)} m`;
}

export function formatDimensions(dimensions: ImageDimensions): string {
  return `${dimensions.width} × ${dimensions.height} px`;
}
